import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FormBuilder,
  useToast,
  Button,
} from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  useInvitationControllerBrokerInvite,
} from 'korint-sdk';
import { Field, useKorintModuleConfig } from 'korint-sdk/react';
import { Send } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';

interface TeamUserInviteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  onSuccess: () => void;
}

export function TeamUserInviteDialog({
  isOpen,
  onClose,
  teamId,
  teamName,
  onSuccess,
}: TeamUserInviteDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const teamManagementConfig = useKorintModuleConfig('teamManagement');

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    enabled: !!user?.brokerageFirmId,
  });

  const form = useForm({
    defaultValues: {
      teamInviteEmail: '',
      teamInviteFirstName: '',
      teamInviteLastName: '',
      brokerRole: '',
    },
  });

  const { mutateAsync: inviteUser, isPending: isInviting } = useInvitationControllerBrokerInvite({
    mutation: {
      onSuccess: () => {
        toast.toast({
          title: t('teamManagement.toastSuccessTitle'),
          description: t('teamManagement.inviteDialog.inviteSuccess'),
        });
        form.reset();
        onSuccess();
      },
      onError: error => {
        toast.toast({
          title: t('teamManagement.toastErrorTitle'),
          description: error.userError || t('teamManagement.inviteDialog.inviteError'),
        });
      },
    },
  });

  // Determine available roles based on firm type
  const availableRoles = useMemo<string[]>(() => {
    if (!brokerageFirm?.firmType) return [];

    const rolesForFirmType =
      teamManagementConfig.inviteRole?.[
        brokerageFirm.firmType as keyof typeof teamManagementConfig.inviteRole
      ];

    // Support both string (legacy) and array (new) formats
    if (Array.isArray(rolesForFirmType)) {
      return rolesForFirmType;
    }

    // Legacy: single string role
    return rolesForFirmType ? [rolesForFirmType] : [];
  }, [teamManagementConfig, brokerageFirm]);

  // Auto-select if single role
  useEffect(() => {
    if (availableRoles.length === 1) {
      form.setValue('brokerRole', availableRoles[0]);
    }
  }, [availableRoles, form]);

  // Build fields array dynamically
  const fields = useMemo<Array<Field>>(() => {
    const baseFields: Array<Field> = [
      {
        name: 'teamInviteEmail',
        DEPRECATED_placeholder: t('teamManagement.inviteDialog.fields.email.placeholder'),
        type: 'email',
        required: true,
      },
      {
        name: 'teamInviteFirstName',
        DEPRECATED_placeholder: t('teamManagement.inviteDialog.fields.firstName.placeholder'),
        type: 'text',
        required: true,
      },
      {
        name: 'teamInviteLastName',
        DEPRECATED_placeholder: t('teamManagement.inviteDialog.fields.lastName.placeholder'),
        type: 'text',
        required: true,
      },
    ];

    // Only show role selector if multiple roles available
    if (availableRoles.length > 1) {
      baseFields.push({
        name: 'brokerRole',
        type: 'select',
        required: true,
        DEPRECATED_placeholder: t('teamManagement.roleFieldPlaceholder'),
        options: availableRoles.map(role => ({
          label: t(`businessRoles.${role}.label`),
          value: role,
        })),
      });
    }

    return baseFields;
  }, [availableRoles, t]);

  // Get the final role value
  const finalRole = form.watch('brokerRole');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('teamManagement.inviteDialog.title', { teamName })}</DialogTitle>
        </DialogHeader>

        <Stack>
          <FormBuilder fields={fields} form={form} isLoading={isInviting} />

          <Group align="stretch" justify="end" gap={2}>
            <Button variant="outline" onClick={onClose} disabled={isInviting}>
              {t('teamManagement.inviteDialog.cancel')}
            </Button>
            <Button
              leftIcon={<Send className="h-4 w-4" />}
              onClick={form.handleSubmit(async data => {
                if (!user?.brokerageFirmId || !finalRole) return;

                await inviteUser({
                  data: {
                    email: data.teamInviteEmail,
                    brokerageFirmId: teamId,
                    firstName: data.teamInviteFirstName,
                    lastName: data.teamInviteLastName,
                    brokerRole: finalRole,
                  },
                });
              })}
              loading={isInviting}
              disabled={!finalRole || !availableRoles.includes(finalRole)}
            >
              {t('teamManagement.inviteDialog.submit')}
            </Button>
          </Group>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
